import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import Logo from "@/components/Logo";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

const BG =
  "https://images.unsplash.com/photo-1787496994323-59ac5cff09f9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBjbGluaWMlMjBpbnRlcmlvcnxlbnwwfHx8fDE3ODk1NTY2NDN8MA&ixlib=rb-4.1.0&q=85";

export default function AdminLogin() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(username.trim(), password.trim());
      if (u.role !== "admin") {
        toast.error("This is not an admin account.");
        return;
      }
      toast.success("Welcome, Admin");
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:block overflow-hidden">
        <img src={BG} alt="Clinic" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#0F172A]/80" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <Logo onDark />
            <span className="font-heading font-extrabold text-xl tracking-tight">Blue Cross · Admin</span>
          </div>
          <div className="max-w-md">
            <p className="text-xs uppercase tracking-[0.3em] text-white/60 mb-4">Control Center</p>
            <h1 className="font-heading text-5xl font-black leading-[1.05] tracking-tighter">
              Manage reps. Track every clinic.
            </h1>
          </div>
          <span />
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm animate-fade-up">
          <Logo className="lg:hidden h-12 w-12 mb-6" />
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">Admin Login</p>
          <h2 className="font-heading text-3xl font-black tracking-tighter text-slate-900 mb-8">Control Panel Access</h2>

          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="user">Username</Label>
              <Input
                id="user"
                data-testid="admin-username-input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="admin"
                className="h-12 bg-slate-50"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="apwd">Password</Label>
              <Input
                id="apwd"
                data-testid="admin-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="h-12 bg-slate-50"
                required
              />
            </div>
            <Button
              type="submit"
              data-testid="admin-login-submit"
              disabled={loading}
              className="w-full h-12 rounded-xl bg-[#0F172A] hover:bg-[#1e293b] text-white font-semibold"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Sign In"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
